import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Input,
  ListItem,
  Switch,
  Text,
  UnorderedList,
  VStack
} from '@chakra-ui/react'
import { arrayMove } from '@dnd-kit/sortable'

import { ActionButton } from '../../../components/ActionButton'
import { FormField } from '../../../components/FormField'
import { DragHandle, SortableList, useSortableRow } from '../../../components/SortableList'
import { JobPositionDocument } from '../../../services/JobPosition/types'
import { DOCUMENT_NAME_MAX_LENGTH, DOCUMENTS_MAX } from './constants'

/** A required-document row being edited. `_id` is the drag key; `id` exists once saved. */
export interface IDocumentRow {
  _id: string
  id?: number
  name: string
  required: boolean
}

export const newDocumentRow = (): IDocumentRow => ({ _id: crypto.randomUUID(), name: '', required: false })

export const toDocumentRows = (documents: JobPositionDocument[]): IDocumentRow[] =>
  documents.map((document) => ({
    _id: crypto.randomUUID(),
    id: document.id,
    name: document.name,
    required: document.required
  }))

/** Client-side check mirroring the API: every row needs a name. Keys follow Laravel (`documents.0.name`). */
export const validateDocuments = (rows: IDocumentRow[]): Record<string, string[]> => {
  const errors: Record<string, string[]> = {}
  rows.forEach((row, index) => {
    if (row.name.trim() === '') errors[`documents.${index}.name`] = ['Informe o nome do documento.']
  })
  return errors
}

interface IJobPositionDocumentsEditorProps {
  rows: IDocumentRow[]
  onChange: (rows: IDocumentRow[]) => void
  fieldErrors?: Record<string, string[]>
  isDisabled?: boolean
  isReadOnly?: boolean
}

/**
 * The documents a new employee attaches in the admission form for this role. "Obrigatório"
 * blocks the admission form until the document is attached.
 */
export function JobPositionDocumentsEditor({
  rows,
  onChange,
  fieldErrors,
  isDisabled = false,
  isReadOnly = false
}: IJobPositionDocumentsEditorProps) {
  if (isReadOnly) {
    return rows.length === 0 ? (
      <Text variant="caption">Nenhum documento exigido</Text>
    ) : (
      <UnorderedList spacing={1} pl={1}>
        {rows.map((row) => (
          <ListItem key={row._id}>
            {row.name}
            {row.required && (
              <Badge ml={2} colorScheme="magenta">
                Obrigatório
              </Badge>
            )}
          </ListItem>
        ))}
      </UnorderedList>
    )
  }

  const update = (_id: string, patch: Partial<IDocumentRow>) =>
    onChange(rows.map((row) => (row._id === _id ? { ...row, ...patch } : row)))

  const isFull = rows.length >= DOCUMENTS_MAX

  return (
    <VStack align="stretch" spacing={3}>
      <Text variant="caption">
        Estes documentos aparecem na etapa Documentos do formulário de admissão de quem for contratado para este cargo.
      </Text>

      {rows.length > 0 && (
        <SortableList ids={rows.map((row) => row._id)} onReorder={(from, to) => onChange(arrayMove(rows, from, to))}>
          <VStack align="stretch" spacing={3}>
            {rows.map((row, index) => (
              <DocumentRow
                key={row._id}
                row={row}
                index={index}
                error={fieldErrors?.[`documents.${index}.name`]}
                isDisabled={isDisabled}
                onChange={(patch) => update(row._id, patch)}
                onRemove={() => onChange(rows.filter((other) => other._id !== row._id))}
              />
            ))}
          </VStack>
        </SortableList>
      )}

      <Box>
        <Button
          variant="outline"
          colorScheme="gray"
          onClick={() => onChange([...rows, newDocumentRow()])}
          isDisabled={isDisabled || isFull}
        >
          + Adicionar documento
        </Button>
      </Box>
    </VStack>
  )
}

interface IDocumentRowProps {
  row: IDocumentRow
  index: number
  error?: string[]
  isDisabled: boolean
  onChange: (patch: Partial<IDocumentRow>) => void
  onRemove: () => void
}

function DocumentRow({ row, index, error, isDisabled, onChange, onRemove }: IDocumentRowProps) {
  const { setNodeRef, style, handleProps } = useSortableRow(row._id)

  return (
    <Box ref={setNodeRef} style={style} border="1px solid" borderColor="gray.200" borderRadius="md" p={3} bg="white">
      <Flex justify="space-between" align="center" mb={2}>
        <HStack spacing={1}>
          <DragHandle {...handleProps} isDisabled={isDisabled} />
          <Text fontWeight="bold" fontSize="sm">
            Documento {index + 1}
          </Text>
        </HStack>
        <ActionButton action="delete" label="Remover documento" onClick={onRemove} isDisabled={isDisabled} mr={0} />
      </Flex>

      <Flex gap={4} align="flex-start" direction={{ base: 'column', md: 'row' }}>
        <FormField label="Nome do Documento" error={error} isRequired flex="1">
          <Input
            size="sm"
            value={row.name}
            onChange={(e) => onChange({ name: e.target.value })}
            maxLength={DOCUMENT_NAME_MAX_LENGTH}
            placeholder="Ex.: RG, CPF, Comprovante de residência"
            isDisabled={isDisabled}
          />
        </FormField>
        <FormField label="Obrigatório" w="auto" mb={0}>
          <Switch
            isChecked={row.required}
            onChange={(e) => onChange({ required: e.target.checked })}
            isDisabled={isDisabled}
          />
        </FormField>
      </Flex>
    </Box>
  )
}
